// Step 3 — review the order before paying. A receipt-style card plus a
// payment hint; the bottom bar repeats the total and carries the Pay button.

import { Button } from "../components/Button";
import {
  ArrowLeft,
  CalendarGlyph,
  CardChipGlyph,
} from "../components/Illustrations";
import { KioskShell } from "../components/KioskShell";
import { useLang } from "../i18n/LangContext";
import { formatCLP, formatDateLong } from "../lib/format";
import { totalAmount, totalTickets } from "../state/reducer";
import type { KioskState } from "../state/types";
import type { Dispatch } from "./shared";

type Review = Extract<KioskState, { phase: "review" }>;

export function ReviewScreen({
  state,
  dispatch,
}: {
  state: Review;
  dispatch: Dispatch;
}) {
  const { t, locale } = useLang();
  const total = totalAmount(state);
  const count = totalTickets(state.quantities);
  const lines = state.ticketTypes
    .map((tt) => ({ tt, qty: state.quantities[tt.id] ?? 0 }))
    .filter((line) => line.qty > 0);

  return (
    <KioskShell onCancel={() => dispatch({ type: "cancel" })} step={3}>
      <div className="screen screen--center">
        <h1 className="screen__title">{t("reviewTitle")}</h1>
        <div className="card receipt">
          <div className="receipt__row receipt__row--head">
            <span className="with-icon">
              <CalendarGlyph size={16} />
              {t("reviewVisitDate")}
            </span>
            <span>{formatDateLong(state.date, locale)}</span>
          </div>
          <div className="receipt__divider" />
          {lines.map(({ tt, qty }) => (
            <div className="receipt__row" key={tt.id}>
              <span>
                {tt.name} × {qty}
              </span>
              <span>{formatCLP(tt.priceCLP * qty, locale)}</span>
            </div>
          ))}
          <div className="receipt__divider" />
          <div className="receipt__total">
            <span>{t("total")}</span>
            <span>{formatCLP(total, locale)}</span>
          </div>
        </div>
        <p className="screen__note with-icon">
          <CardChipGlyph size={18} />
          {t("reviewPayHint")}
        </p>
      </div>
      <div className="bottom-bar">
        <div className="bottom-bar__summary">
          <span className="bottom-bar__total">{formatCLP(total, locale)}</span>
          <span className="bottom-bar__count">
            {count === 1
              ? t("ticketCountOne", { n: count })
              : t("ticketCountMany", { n: count })}
          </span>
        </div>
        <div className="bottom-bar__actions">
          <Button
            icon={<ArrowLeft />}
            onClick={() => dispatch({ type: "go-back" })}
            variant="ghost"
          >
            {t("back")}
          </Button>
          <Button onClick={() => dispatch({ type: "submit-order" })} size="lg">
            {t("pay")}
          </Button>
        </div>
      </div>
    </KioskShell>
  );
}
